import { useState } from "react";
import { Cpu, Linkedin, Send } from "lucide-react";
import { toast } from "sonner";
import { CTA } from "@/constants/testIds";

const cols = [
  {
    title: "Company",
    links: ["About Us", "Careers", "Blog", "Contact"],
  },
  {
    title: "Services",
    links: ["AI Solutions", "IoT Solutions", "Cloud & DevOps", "Edge Computing"],
  },
  {
    title: "Solutions",
    links: ["Manufacturing", "Healthcare", "Smart Cities", "Energy"],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");

  const subscribe = (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    toast.success("Subscribed! Look out for the next Spoorti newsletter.");
    setEmail("");
  };

  return (
    <footer data-testid="site-footer" className="relative border-t border-white/10 bg-[#0B0F19] pt-20 pb-10 overflow-hidden">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-cyan-500/10 blur-3xl rounded-full pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-500 grid place-items-center">
                <Cpu className="w-5 h-5 text-white" />
              </span>
              <span className="font-heading font-bold text-xl">
                Spoorti<span className="gradient-text"> Technologies</span>
              </span>
            </div>
            <p className="mt-4 text-slate-400 max-w-md leading-relaxed">
              Transforming industries with AI-powered IoT solutions, edge computing and
              cloud-native platforms — at enterprise scale.
            </p>

            <form onSubmit={subscribe} className="mt-6 flex w-full max-w-md gap-2">
              <input
                data-testid={CTA.newsletterInput}
                type="text"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 bg-[#131C2F] border border-white/10 rounded-full px-5 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/60"
              />
              <button
                type="submit"
                data-testid={CTA.newsletterSubmit}
                className="btn-primary inline-flex items-center justify-center gap-2 text-white px-5 py-3 rounded-full text-sm font-semibold"
              >
                Subscribe <Send className="w-4 h-4" />
              </button>
            </form>

            <div className="mt-6 flex items-center gap-3">
              {[
                {
                  Icon: Linkedin,
                  href: "https://www.linkedin.com/company/spoorti-technologies/?viewAsMember=true",
                  label: "LinkedIn",
                },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  data-testid={`social-${label.toLowerCase()}`}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-white/10 grid place-items-center text-slate-300 hover:text-white hover:border-cyan-500/40 hover:bg-cyan-500/10 transition"
                  aria-label={label}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {cols.map((c) => (
              <div key={c.title}>
                <h4 className="font-heading text-sm uppercase tracking-[0.2em] text-cyan-400 mb-4">
                  {c.title}
                </h4>
                <ul className="space-y-2.5">
                  {c.links.map((l) => (
                    <li key={l}>
                      <a
                        href="#"
                        className="text-sm text-slate-300 hover:text-white transition"
                      >
                        {l}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} Spoorti Technologies. All rights reserved.</div>
          <div className="flex gap-5">
            <a href="#" className="hover:text-white transition">Privacy</a>
            <a href="#" className="hover:text-white transition">Terms</a>
            <a href="mailto:info@spoortitechnologies.in" className="hover:text-white transition">
              info@spoortitechnologies.in
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
